import React from 'react';
import {
    SimpleGrid,
    Card,
    CardBody,
    Stat,
    StatLabel,
    StatNumber,
    StatHelpText,
    VStack,
    HStack,
    Badge,
    Progress,
} from '@chakra-ui/react';
import type { MappedDashboardStats } from "@modules/ytg/hooks/useYtgDashboardStats";

interface YtgStatsCardsProps {
    stats: MappedDashboardStats | null | undefined;
}

/**
 * Main YTG statistics cards.
 * Displays the total number of operations and the completion rate.
 */
export const YtgStatsCards = ({ stats }: YtgStatsCardsProps): JSX.Element | null => {
    if (!stats) return null;

    return (
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6} mb={6}>
            {/* Total operations */}
            <Card>
                <CardBody>
                    <Stat>
                        <StatLabel color="gray.600">Total opérations</StatLabel>
                        <StatNumber fontSize="3xl" color="brand.ytg.main">
                            {stats.total_operations}
                        </StatNumber>
                        <StatHelpText>
                            <VStack spacing={2} mt={2} align="stretch">
                                <HStack spacing={2} flexWrap="wrap">
                                    <Badge colorScheme="green">
                                        {stats.by_status.completed} terminées
                                    </Badge>
                                    <Badge colorScheme="blue">
                                        {stats.by_status.in_progress} en cours
                                    </Badge>
                                    <Badge colorScheme="orange">
                                        {stats.by_status.pending} en attente
                                    </Badge>
                                    <Badge colorScheme="red">
                                        {stats.by_status.failed} échouées
                                    </Badge>
                                </HStack>
                            </VStack>
                        </StatHelpText>
                    </Stat>
                </CardBody>
            </Card>

            {/* Completion rate */}
            <Card>
                <CardBody>
                    <Stat>
                        <StatLabel color="gray.600">Taux de complétion</StatLabel>
                        <StatNumber fontSize="3xl" color="blue.600">
                            {stats.completion_rate.toFixed(0)}%
                        </StatNumber>
                        <Progress
                            value={stats.completion_rate}
                            colorScheme="blue"
                            size="sm"
                            mt={2}
                            borderRadius="md"
                        />
                    </Stat>
                </CardBody>
            </Card>
        </SimpleGrid>
    );
};
